import { Box, Button, Chip, CircularProgress, FormControl, Grid, InputLabel, Link, makeStyles, MenuItem, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography } from "@material-ui/core";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import Pagination from "@material-ui/lab/Pagination";
import { PrintOutlined } from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import CircularCenteredLoader from "features/common/CircularCenteredLoader";
import validateDateAndFormat from "utils/validateDateAndFormat";
import fileDownload from "js-file-download";
import instance from "api/globalaxios";
import DateRangePickerACCB from "./DateRangePickerACCB";
import {
  BAC_BOOKING_STATUS_OPTIONS,
  BAC_CATEGORY_OPTIONS,
  BAC_TYPE_OPTIONS,
  getBacUndatedCases,
  printBacUndatedReport,
  readApiError,
} from "./BacReportApi";
import { formatPartyName } from "Clients/ACCB/utils";
import GlobalSearch from "../../Components/GlobalSearch/GlobalSearch";
import FormError from "../../Components/FormError";
import useBACPermissions from "../../hooks/usePermissions";
import { ADMIN_TABS, navigateToAdminTab } from "../adminTabNavigation";

const PAGE_SIZE = 10;
const DATE_RANGE_ERROR = "End date must be greater than Start date.";

// Hearing Status chip colours, keyed by the API's booking_status.
const BOOKING_STATUS_STYLE = {
  never: { backgroundColor: "#FDECEA", color: "#C62828" },
  lapsed: { backgroundColor: "#FFF4E5", color: "#B26A00" },
};

const useStyles = makeStyles((theme) => ({
  filtersContainer: {
    padding: '1rem',
    borderBottom: '1px solid black',
    display: 'flex',
    gap: '15px',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: 'auto',
    // Four dropdowns plus the dates and Print do not fit one row, so wrap.
    flexWrap: 'wrap',
  },
  filterSelect: {
    flex: '1 1 160px',
    maxWidth: 220,
    [theme.breakpoints.down('sm')]: {
      maxWidth: 'none',
    },
  },
  buttonPrint: {
    borderRadius: '0.5rem',
    border: '1px solid #AAAFB9',
    background: '#fff',
    padding: '0.5rem 1rem',
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '1.5rem 8px',
  },
}));

export default function BacUndatedReport() {
  const classes = useStyles();
  const history = useHistory();
  const { can } = useBACPermissions();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cases, setCases] = useState([]);
  const [category, setCategory] = useState('All');
  const [caseType, setCaseType] = useState('All');
  const [bookingStatus, setBookingStatus] = useState('All');
  const [stageId, setStageId] = useState('All');
  const [stages, setStages] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState("");

  // Stage filter options. StageInputField has no "All" choice, so load the
  // same list here.
  useEffect(() => {
    instance
      .get('/bac/masters/arbitration-stage/', { params: { pagination: false } })
      .then((res) => setStages(res?.data?.results || []))
      .catch((err) => console.error('Error fetching stages', err));
  }, []);

  const tableHeadings = [
    'S.No.', 'Case Number', 'Category', 'Claimant(s)', 'Respondent(s)', 'Arbitrator(s)',
    'Stage', 'Registered On', 'Hearing Status', 'Last Hearing', 'Days Without Hearing',
  ];

  // Open the case in View Case, as Overview does when a case is clicked.
  const openCase = (caseId) => {
    sessionStorage.setItem("bac_overview_caseid", caseId);
    navigateToAdminTab(history, ADMIN_TABS.OVERVIEW);
  };

  const renderCaseNumber = (item) => {
    if (!item?.case_id || !can("case_overview")) return item?.case_number || '—';
    return (
      <Link component="button" variant="body2" onClick={() => openCase(item.case_id)} style={{ textAlign: 'left' }}>
        {item.case_number}
      </Link>
    );
  };

  const renderBookingStatus = (item) => (
    <Chip
      size="small"
      label={item?.booking_status_label || '—'}
      style={{ fontWeight: 600, ...(BOOKING_STATUS_STYLE[item?.booking_status] || {}) }}
    />
  );

  const processResponse = (item, index, page) => [
    (page - 1) * PAGE_SIZE + index + 1,
    renderCaseNumber(item),
    item?.category || '—',
    formatPartyName(item?.claimant_name) || '—',
    formatPartyName(item?.respondent_name) || '—',
    item?.arbitrator_name || '—',
    item?.current_stage || '—',
    item?.registered_on || '—',
    renderBookingStatus(item),
    item?.last_hearing_date || '—',
    item?.days_without_hearing ?? '—',
  ];

  const isDateRangeInvalid = startDate && endDate && new Date(endDate) < new Date(startDate);

  const filterArgs = () => [
    category,
    caseType,
    validateDateAndFormat(startDate, 'yyyy-MM-dd'),
    validateDateAndFormat(endDate, 'yyyy-MM-dd'),
    stageId,
    bookingStatus,
  ];

  const reload = (page, searchText = search) => {
    if (isDateRangeInvalid) {
      setCases([]);
      setTotalCount(0);
      setError(DATE_RANGE_ERROR);
      return;
    }
    setError("");
    setLoading(true);
    getBacUndatedCases(...filterArgs(), page, searchText)
      .then((res) => {
        setCases(res?.data?.results || []);
        setTotalCount(res?.data?.count || 0);
        setLoading(false);
      })
      .catch(async (err) => {
        console.log('Error: ', err);
        setCases([]);
        setTotalCount(0);
        setError((await readApiError(err)) || "Failed to load the report. Please try again.");
        setLoading(false);
      });
  };

  useEffect(() => {
    setCurrentPage(1);
    reload(1, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, category, caseType, stageId, bookingStatus, search]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    reload(page, search);
  };

  const handleSearchChange = (val) => {
    setSearch(val);
    setCurrentPage(1);
  };

  const handlePrintReport = () => {
    if (isDateRangeInvalid) {
      setError(DATE_RANGE_ERROR);
      return;
    }
    setPrinting(true);
    printBacUndatedReport(...filterArgs(), search)
      .then((res) => {
        fileDownload(res.data, 'undated_cases_report.pdf');
        setPrinting(false);
      })
      .catch(async (err) => {
        console.log(err);
        setError((await readApiError(err)) || "Failed to download the report. Please try again.");
        setPrinting(false);
      });
  };

  return (
    <Box>
      <Typography style={{ fontSize: '1.5rem', padding: '2rem' }}>
        UNDATED CASES REPORT
      </Typography>
      <Box style={{ backgroundColor: 'white' }}>
        <Box style={{ padding: '1rem 1rem 0 1rem', display: 'flex', justifyContent: 'flex-end' }}>
          <GlobalSearch
            value={search}
            onSearchChange={handleSearchChange}
            placeholder="Search Undated Cases..."
          />
        </Box>
        <Box className={classes.filtersContainer}>
          <FormControl fullWidth size="small" margin="none" variant="outlined" className={classes.filterSelect}>
            <InputLabel>Category</InputLabel>
            <Select label="Category" name="category" value={category} onChange={(e) => setCategory(e.target.value)}>
              {BAC_CATEGORY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" margin="none" variant="outlined" className={classes.filterSelect}>
            <InputLabel>Type</InputLabel>
            <Select label="Type" name="case_type" value={caseType} onChange={(e) => setCaseType(e.target.value)}>
              {BAC_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" margin="none" variant="outlined" className={classes.filterSelect}>
            <InputLabel>Hearing Status</InputLabel>
            <Select label="Hearing Status" name="booking_status" value={bookingStatus} onChange={(e) => setBookingStatus(e.target.value)}>
              {BAC_BOOKING_STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" margin="none" variant="outlined" className={classes.filterSelect}>
            <InputLabel>Stage</InputLabel>
            <Select label="Stage" name="stage_id" value={stageId} onChange={(e) => setStageId(e.target.value)}>
              <MenuItem value="All">All</MenuItem>
              {stages.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.stage}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <DateRangePickerACCB startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate} />
          <Button
            className={classes.buttonPrint}
            size="small"
            endIcon={printing ? <CircularProgress size={16} color="inherit" /> : <PrintOutlined />}
            onClick={handlePrintReport}
            disabled={printing}
          >
            {printing ? "Printing..." : "Print Report"}
          </Button>
        </Box>
        <Box>
          {error && (
            <Box style={{ padding: '0 1rem' }}>
              <FormError message={error} />
            </Box>
          )}
          <Box style={{ padding: '0.5rem 1.5rem' }}>
            <Typography style={{ fontSize: '0.875rem', color: TEXT.muted, textAlign: "right" }}>
              Total: {totalCount}
            </Typography>
          </Box>
          {loading ? (
            <Box style={{ margin: 'auto', width: '100%', padding: '2rem 0' }}>
              <CircularCenteredLoader />
            </Box>
          ) : (
            cases.length !== 0 ? (
              <TableContainer component={Paper} style={{ boxShadow: 'none', border: '1px solid #E0E0E0', overflowX: 'auto' }}>
                <Table size="small" style={{ maxWidth: '100%', marginTop: 0 }}>
                  <TableHead>
                    <TableRow style={{ backgroundColor: '#F2F2F2' }}>
                      {tableHeadings.map((heading) => (
                        <TableCell key={heading} style={{ fontWeight: 600, color: TEXT.strong, fontSize: '0.875rem', padding: '12px 8px', whiteSpace: 'nowrap' }}>
                          {heading}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cases.map((dispute, index) => (
                      <TableRow key={dispute?.case_id || index} hover style={{ backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#FAFAFA' }}>
                        {processResponse(dispute, index, currentPage).map((item, idx) => (
                          <TableCell key={idx} style={{ color: TEXT.strong, fontSize: '0.875rem', padding: '12px 8px', whiteSpace: 'pre-line', verticalAlign: 'top' }}>
                            {item}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Grid container justify="center">
                <Grid item>
                  <Box textAlign="center">
                    <Typography style={{ padding: '2rem 8px', textAlign: 'center', fontSize: '1.5rem' }}>
                      No undated case is found for this criteria.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )
          )}
          {!loading && totalCount > PAGE_SIZE && (
            <Box className={classes.paginationContainer}>
              <Pagination
                count={Math.ceil(totalCount / PAGE_SIZE)}
                page={currentPage}
                onChange={(e, page) => handlePageChange(page)}
                color="primary"
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
